import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { query } from '../api/client';
import {
  useArtists,
  useBreakdown,
  useChartmetric,
  useComposition,
  useEvents,
  usePeers,
  useSummary,
} from '../api/queries';
import { ConcentrationDonut, MarketsDonut, VintageDonut } from '../components/ArtistDonuts';
import { ChartmetricStrip } from '../components/ChartmetricStrip';
import { CompositionChart } from '../components/CompositionChart';
import { CopyButton, DownloadLink } from '../components/CopyButton';
import { EventsTable } from '../components/EventsTable';
import { GrowthTable } from '../components/GrowthTable';
import { Header } from '../components/Header';
import { Hero } from '../components/Hero';
import { MethodNotes } from '../components/MethodNotes';
import { PeerPicker, PeersTable } from '../components/PeersTable';
import { ProblemBanner } from '../components/ProblemBanner';
import { Seg } from '../components/Seg';
import { SelectorsCard } from '../components/SelectorsCard';
import { fmtM, territoryName } from '../lib/format';
import { artistRoute } from '../router';
import { peerIds, type Search, selectorsOf } from '../state/search';

export function ArtistPage() {
  const { artistId } = artistRoute.useParams();
  const search = artistRoute.useSearch();
  const navigate = useNavigate({ from: artistRoute.fullPath });
  const setSearch = (patch: Partial<Search>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }) as Search, replace: true });
  const goArtist = (id: string) =>
    navigate({ to: '/a/$artistId', params: { artistId: id }, search: (prev) => prev as Search });

  const sel = selectorsOf(search);
  const peers = peerIds(search);
  const artists = useArtists();
  const summary = useSummary(artistId);
  const cm = useChartmetric(artistId);
  const breakdown = useBreakdown(artistId, sel);
  const composition = useComposition(artistId, sel);
  const events = useEvents(artistId, search.ev);
  const peerTable = usePeers(artistId, search.cat, peers, search.peerSetId);

  useEffect(() => {
    try {
      localStorage.setItem('lastArtistId', artistId);
    } catch {
      /* private mode */
    }
  }, [artistId]);

  const s = summary.data?.data;
  const hasUs = s?.territories.includes('US') ?? true;
  useEffect(() => {
    if (s && !hasUs && search.territory !== 'WW') setSearch({ territory: 'WW' });
  });

  const exportUrl = (kind: string) =>
    `/api/artists/${artistId}/export/${kind}${query({ ...sel, format: 'tsv' })}`;
  const b = breakdown.data?.data;

  return (
    <>
      <Header anchor={s?.anchor} />
      <div className="wrap">
        <section>
          <div className="card inputs">
            <div className="field">
              <label htmlFor="f-artist">Artist</label>
              <select id="f-artist" value={artistId} onChange={(e) => goArtist(e.target.value)}>
                {(artists.data?.data ?? [{ artistId, artistName: artistId }]).map((a) => (
                  <option key={a.artistId} value={a.artistId}>
                    {a.artistName}
                  </option>
                ))}
              </select>
              <div className="hint">
                Select artist — everything in this block is artist-level [A]
              </div>
            </div>
          </div>
          {summary.isError ? <ProblemBanner error={summary.error} /> : null}
          {s ? (
            <>
              <Hero summary={s} cm={cm.data?.data ?? null} />
              <div className="dgrid">
                <MarketsDonut summary={s} />
                <ConcentrationDonut summary={s} />
                <VintageDonut summary={s} />
              </div>
              <ChartmetricStrip cm={cm.data?.data ?? null} loading={cm.isPending} />
            </>
          ) : summary.isPending ? (
            <div className="skeleton">
              Aggregating {artistId}… the first load of an artist takes a few seconds.
            </div>
          ) : null}
        </section>

        <section>
          <div className="secband">
            <h2>Song breakdown</h2>
            <span className="unit">
              {b
                ? `basis ${b.basisWindow.label} · ${territoryName(b.selectors.territory)} · ${fmtM(b.denominator)}M streams total`
                : '—'}
            </span>
            <div className="tools">
              <CopyButton label="Copy levels for Excel" url={exportUrl('levels')} />
              <DownloadLink label="Download levels" url={exportUrl('levels')} />
            </div>
          </div>
          <SelectorsCard
            search={search}
            hasUs={hasUs}
            anchorYear={Number((s?.anchor ?? '2026').slice(0, 4))}
            onChange={setSearch}
          />
          <div className="legend">
            {b?.notes.overrides ? (
              <span>
                <b className="ovr">◆</b>&nbsp;commercial release date, researched override
              </span>
            ) : null}
          </div>
        </section>

        <section>
          <div className="blockhead">
            <h2>% YoY</h2>
            <span className="unit">like-for-like comparisons only · 2022 onward</span>
            <div className="tools">
              <CopyButton label="Copy for Excel" url={exportUrl('growth')} />
            </div>
          </div>
          {breakdown.isError ? <ProblemBanner error={breakdown.error} /> : null}
          {b ? (
            <GrowthTable b={b} />
          ) : breakdown.isPending ? (
            <div className="loading">Loading breakdown…</div>
          ) : null}
        </section>

        <section>
          <div className="secband">
            <h2>Events</h2>
            <span className="unit">
              {events.data
                ? `${events.data.data.events.length} on existing songs · worldwide · ranked by streams gained`
                : '—'}
            </span>
            <div className="tools">
              <Seg
                value={search.ev}
                options={[
                  { v: 'all', label: 'All' },
                  { v: 'revival', label: 'Revivals' },
                  { v: 'campaign', label: 'Campaigns' },
                ]}
                onChange={(ev) => setSearch({ ev })}
              />
            </div>
          </div>
          {events.isError ? <ProblemBanner error={events.error} /> : null}
          {events.data ? <EventsTable data={events.data.data} /> : null}
          <p className="evnote">
            A month is an event for a track when it beats its own six-month median by 50% or more
            and gains at least 3M streams. New releases are excluded — a song beating its own
            baseline in its first weeks is only its release curve. What remains are events on songs
            already out: <b>campaigns</b> (4–24 months old, still in the release cycle) and{' '}
            <b>revivals</b> (over two years old — a sync, playlist or social trend). <b>Kept</b> is
            how much of the lift survived six months on.
          </p>
        </section>

        <section>
          <div className="secband">
            <h2>Composition over time</h2>
            <span className="unit">
              {composition.data
                ? `worldwide · monthly · ${composition.data.data.months[0]} to ${composition.data.data.months.at(-1)}`
                : '—'}
            </span>
            <div className="tools">
              <Seg
                value={search.stack}
                options={[
                  { v: 'abs', label: 'Streams' },
                  { v: 'pct', label: 'Share' },
                ]}
                onChange={(stack) => setSearch({ stack })}
              />
              <CopyButton
                label="Copy daily data"
                url={exportUrl('daily')}
                done={(t) => `Copied ${(t.split('\n').length - 1).toLocaleString('en-US')} days`}
              />
              <DownloadLink label="Download daily" url={exportUrl('daily')} />
            </div>
          </div>
          {composition.isError ? <ProblemBanner error={composition.error} /> : null}
          {composition.data ? (
            <CompositionChart c={composition.data.data} mode={search.stack} />
          ) : null}
        </section>

        <section>
          <div className="secband">
            <h2>Compared with peers</h2>
            <span className="unit">
              {peerTable.data
                ? `${peerTable.data.data.rows.length} artists · worldwide · catalogue before ${search.cat} · ${s?.artistName ?? artistId} ranks ${
                    [...peerTable.data.data.rows]
                      .sort((a, c) => c.total.ltm - a.total.ltm)
                      .findIndex((r) => r.isSubject) + 1
                  } of ${peerTable.data.data.rows.length}`
                : '—'}
            </span>
            <div className="tools">
              <Seg
                value={search.peerSort}
                options={[
                  { v: 'tot', label: 'Total' },
                  { v: 'cat', label: 'Catalogue' },
                  { v: 'cagr', label: 'Cat. CAGR' },
                ]}
                onChange={(peerSort) => setSearch({ peerSort })}
              />
            </div>
          </div>
          {artists.data ? (
            <PeerPicker
              artists={artists.data.data}
              subjectId={artistId}
              peers={peers}
              onChange={(p) =>
                setSearch({ peers: p.length ? p.join(',') : undefined, peerSetId: undefined })
              }
            />
          ) : null}
          {peerTable.isError ? <ProblemBanner error={peerTable.error} /> : null}
          {peerTable.data ? <PeersTable data={peerTable.data.data} sort={search.peerSort} /> : null}
          <p className="evnote">
            The sheet hangs its peer comparison off <b>Total Artist (Catalogue)</b>, so that row is
            the one to read. Worldwide for every artist, each computed at this artist's anchor where
            the peer holds it. A <span className="ovr">~</span> marks an artist with no researched
            release dates, whose catalogue split rests on raw Luminate vintages and is indicative
            only.
          </p>
        </section>

        {s ? (
          <MethodNotes summary={s} breakdown={b} notes={summary.data?.meta.notes ?? []} />
        ) : null}
      </div>
    </>
  );
}
